import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  assertSafeSessionValue,
  defineNavigationQuerySchema,
  navigationQuery,
  useNavigationQuery,
} from '../../navigation';
import type { SafeSessionValue, SessionStateAdapter } from '../../navigation';

const SEARCH_MAX = 120;
const YEAR_MIN = 2000;
const YEAR_MAX = 2100;
const COUNT_MAX = 100_000;

export type GroupStatus = '' | 'running' | 'planned' | 'finished' | 'canceled';
export type GroupPanel = 'details' | 'edit' | 'quick-add';
export type StudentTab = 'details' | 'groups' | 'account' | 'actions' | 'comments' | 'history';
export type QuarterPanel = '' | 'filters' | 'create' | 'settings';
export type StudentsCountComparison = 'more' | 'less' | 'equal';

/** Stable opaque key for scroll state; raw filters/search terms never reach storage. */
export function receptionNavigationViewKey(
  namespace: string,
  parts: ReadonlyArray<string | number | boolean | null | undefined>,
): string {
  const safeNamespace = /^[a-z0-9-]{1,48}$/u.test(namespace) ? namespace : 'reception-view';
  const text = JSON.stringify(parts);
  let left = 0xdeadbeef ^ text.length;
  let right = 0x41c6ce57 ^ text.length;
  for (let index = 0; index < text.length; index += 1) {
    const code = text.charCodeAt(index);
    left = Math.imul(left ^ code, 2654435761);
    right = Math.imul(right ^ code, 1597334677);
  }
  left = Math.imul(left ^ (left >>> 16), 2246822507)
    ^ Math.imul(right ^ (right >>> 13), 3266489909);
  right = Math.imul(right ^ (right >>> 16), 2246822507)
    ^ Math.imul(left ^ (left >>> 13), 3266489909);
  const digest = (4294967296 * (2097151 & right) + (left >>> 0)).toString(36);
  return `${safeNamespace}:v1:${digest}`;
}

/** Query caches must never cross users, shells, or resolved permission scopes. */
export function receptionAccessCacheScope(input: {
  scopeId: string;
  userId: string | number;
  basename: string;
  profileResolved: boolean;
  userBranch: string;
  showSuper: boolean;
  isGeneralManager: boolean;
  showLanguage: boolean;
}): string {
  return JSON.stringify([
    input.scopeId,
    String(input.userId),
    input.basename,
    input.profileResolved ? 1 : 0,
    input.userBranch.trim().toLocaleLowerCase('en-US'),
    input.showSuper ? 1 : 0,
    input.isGeneralManager ? 1 : 0,
    input.showLanguage ? 1 : 0,
  ]);
}

export function isReceptionBranchAllowed(
  branch: string,
  access: {
    basename: string;
    profileResolved: boolean;
    showSuper: boolean;
    isGeneralManager?: boolean;
    showLanguage: boolean;
    userBranch: string;
  },
): boolean {
  if (!access.profileResolved
    || access.showSuper
    || access.isGeneralManager
    || access.showLanguage) return true;
  if (!branch) return true;
  const assignedBranch = access.userBranch.trim().toLowerCase();
  if (!assignedBranch) return false;
  const requestedBranch = branch.trim().toLowerCase();
  return requestedBranch === assignedBranch || requestedBranch === 'online';
}

const sharedGroupFields = {
  delivery_mode: navigationQuery.string('delivery_mode', { defaultValue: '', maxLength: 20 }),
  q: navigationQuery.string('q', { defaultValue: '', maxLength: SEARCH_MAX }),
  status: navigationQuery.string('status', { defaultValue: '', maxLength: 16 }),
  branch: navigationQuery.string('branch', { defaultValue: '', maxLength: 100 }),
  course: navigationQuery.string('course', { defaultValue: '', maxLength: 150 }),
  instructor: navigationQuery.id('instructor'),
  dateFrom: navigationQuery.date('dateFrom'),
  dateTo: navigationQuery.date('dateTo'),
  month: navigationQuery.string('month', { defaultValue: '', maxLength: 7 }),
  page: navigationQuery.integer('page', { defaultValue: 1, min: 1, max: 100_000 }),
  pageSize: navigationQuery.enum('pageSize', ['10', '25', '50', '100'] as const, '10'),
  group: navigationQuery.id('group'),
  groupPanel: navigationQuery.enum(
    'groupPanel',
    ['details', 'edit', 'quick-add'] as const,
    'details',
  ),
};

export const groupsNavigationSchema = defineNavigationQuerySchema({
  ...sharedGroupFields,
  student: navigationQuery.id('student'),
  studentTab: navigationQuery.enum(
    'studentTab',
    ['details', 'groups', 'account', 'actions', 'comments', 'history'] as const,
    'details',
  ),
});

export const quarterNavigationSchema = defineNavigationQuerySchema({
  q: sharedGroupFields.q,
  status: sharedGroupFields.status,
  branch: sharedGroupFields.branch,
  delivery_mode: sharedGroupFields.delivery_mode,
  course: sharedGroupFields.course,
  instructor: sharedGroupFields.instructor,
  dateFrom: sharedGroupFields.dateFrom,
  dateTo: sharedGroupFields.dateTo,
  month: sharedGroupFields.month,
  year: navigationQuery.id('year'),
  studentsCount: navigationQuery.id('students_count'),
  studentsCountCmp: navigationQuery.string('students_count_cmp', { defaultValue: '', maxLength: 12 }),
  page: sharedGroupFields.page,
  pageSize: sharedGroupFields.pageSize,
  group: sharedGroupFields.group,
  groupPanel: sharedGroupFields.groupPanel,
  panel: navigationQuery.enum(
    'panel',
    ['', 'filters', 'create', 'settings'] as const,
    '',
  ),
});

export const calendarNavigationSchema = defineNavigationQuerySchema({
  month: navigationQuery.string('month', { defaultValue: '', maxLength: 7 }),
  branch: navigationQuery.string('branch', { defaultValue: '', maxLength: 100 }),
  day: navigationQuery.date('day'),
});

function validMonth(value: string): value is string {
  return /^(20\d{2}|2100)-(0[1-9]|1[0-2])$/u.test(value);
}

function validDate(value: string | null): value is string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/u.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

function normalizeStatus(value: string): GroupStatus {
  const normalized = value.trim().toLowerCase();
  if (normalized === 'active') return 'running';
  if (normalized === 'completed') return 'finished';
  if (normalized === 'cancelled') return 'canceled';
  if (normalized === 'running' || normalized === 'planned' || normalized === 'finished' || normalized === 'canceled') {
    return normalized;
  }
  return '';
}

function normalizeComparison(value: string): StudentsCountComparison | '' {
  const normalized = value.trim().toLowerCase();
  if (['more', 'gt', 'gte', 'min'].includes(normalized)) return 'more';
  if (['less', 'lt', 'lte', 'max'].includes(normalized)) return 'less';
  if (normalized === 'equal' || normalized === 'eq') return 'equal';
  return '';
}

function updateParam(params: URLSearchParams, key: string, value: string | number | null) {
  if (value === null || value === '' || value === 0) params.delete(key);
  else params.set(key, String(value));
}

function normalizedSearch(search: string, kind: 'groups' | 'quarter' | 'calendar'): string {
  const params = new URLSearchParams(search);
  if (kind !== 'calendar') {
    updateParam(params, 'status', normalizeStatus(params.get('status') ?? ''));

    const legacyFrom = params.get('date_from');
    const legacyTo = params.get('date_to');
    if (!params.has('dateFrom') && validDate(legacyFrom)) params.set('dateFrom', legacyFrom);
    if (!params.has('dateTo') && validDate(legacyTo)) params.set('dateTo', legacyTo);
    params.delete('date_from');
    params.delete('date_to');

    const month = params.get('month') ?? '';
    if (!validMonth(month)) params.delete('month');
    if (params.has('month')) {
      params.delete('dateFrom');
      params.delete('dateTo');
      if (kind === 'quarter') params.delete('year');
    }

    if (!params.has('group')) {
      params.delete('groupPanel');
      if (kind === 'groups') {
        params.delete('student');
        params.delete('studentTab');
      }
    } else if (kind === 'groups' && !params.has('student')) {
      params.delete('studentTab');
    }

    if (kind === 'groups' && params.has('student')) {
      params.delete('groupPanel');
    }

    if (kind === 'quarter') {
      const year = Number(params.get('year'));
      if (!Number.isSafeInteger(year) || year < YEAR_MIN || year > YEAR_MAX) params.delete('year');
      if (params.has('year')) {
        params.delete('month');
        params.delete('dateFrom');
        params.delete('dateTo');
      }
      const count = Number(params.get('students_count'));
      if (!Number.isSafeInteger(count) || count <= 0 || count > COUNT_MAX) {
        params.delete('students_count');
        params.delete('students_count_cmp');
      } else {
        updateParam(params, 'students_count_cmp', normalizeComparison(params.get('students_count_cmp') ?? '') || 'more');
      }
      if (params.has('group')) params.delete('panel');
    }
  } else {
    const month = params.get('month') ?? '';
    if (!validMonth(month)) params.delete('month');
    const day = params.get('day');
    if (!validDate(day)) {
      params.delete('day');
    } else {
      params.set('month', day.slice(0, 7));
    }
  }
  const value = params.toString();
  return value ? `?${value}` : '';
}

function useCanonicalSearch(kind: 'groups' | 'quarter' | 'calendar') {
  const location = useLocation();
  const navigate = useNavigate();
  const canonicalSearch = React.useMemo(
    () => normalizedSearch(location.search, kind),
    [kind, location.search],
  );
  React.useEffect(() => {
    if (canonicalSearch === location.search) return;
    void navigate({
      pathname: location.pathname,
      search: canonicalSearch,
      hash: location.hash,
    }, { replace: true, state: location.state });
  }, [canonicalSearch, location.hash, location.pathname, location.search, location.state, navigate]);
  return canonicalSearch;
}

function stringParam(params: URLSearchParams, key: string, maxLength: number): string {
  const value = params.get(key) ?? '';
  return value.length <= maxLength ? value : '';
}

function idParam(params: URLSearchParams, key: string): number | null {
  const raw = params.get(key);
  if (!raw || !/^(?:[1-9]\d*)$/u.test(raw)) return null;
  const value = Number(raw);
  return Number.isSafeInteger(value) && value > 0 ? value : null;
}

function pageParam(params: URLSearchParams): number {
  const value = idParam(params, 'page');
  return value && value <= 100_000 ? value : 1;
}

function pageSizeParam(params: URLSearchParams): '10' | '25' | '50' | '100' {
  const value = params.get('pageSize');
  return value === '25' || value === '50' || value === '100' ? value : '10';
}

function groupPanelParam(params: URLSearchParams): GroupPanel {
  const value = params.get('groupPanel');
  return value === 'edit' || value === 'quick-add' ? value : 'details';
}

function dateParam(params: URLSearchParams, key: string): string | null {
  const value = params.get(key);
  return validDate(value) ? value : null;
}

export function useGroupsNavigation() {
  const canonicalSearch = useCanonicalSearch('groups');
  const navigation = useNavigationQuery(groupsNavigationSchema);
  const params = React.useMemo(() => new URLSearchParams(canonicalSearch), [canonicalSearch]);
  const group = idParam(params, 'group');
  const student = group ? idParam(params, 'student') : null;
  const studentTabValue = params.get('studentTab');
  const studentTab: StudentTab = student && (studentTabValue === 'groups'
    || studentTabValue === 'account'
    || studentTabValue === 'actions'
    || studentTabValue === 'comments'
    || studentTabValue === 'history')
    ? studentTabValue
    : 'details';
  return React.useMemo(() => ({
    ...navigation,
    values: {
      q: stringParam(params, 'q', SEARCH_MAX),
      status: normalizeStatus(params.get('status') ?? ''),
      branch: stringParam(params, 'branch', 100),
      delivery_mode: stringParam(params, 'delivery_mode', 20),
      course: stringParam(params, 'course', 150),
      instructor: idParam(params, 'instructor'),
      dateFrom: dateParam(params, 'dateFrom'),
      dateTo: dateParam(params, 'dateTo'),
      month: validMonth(params.get('month') ?? '') ? params.get('month')! : '',
      page: pageParam(params),
      pageSize: pageSizeParam(params),
      group,
      groupPanel: groupPanelParam(params),
      student,
      studentTab,
    },
  }), [group, navigation, params, student, studentTab]);
}

export function useQuarterNavigation() {
  const canonicalSearch = useCanonicalSearch('quarter');
  const navigation = useNavigationQuery(quarterNavigationSchema);
  const params = React.useMemo(() => new URLSearchParams(canonicalSearch), [canonicalSearch]);
  const group = idParam(params, 'group');
  const panelValue = params.get('panel');
  const year = idParam(params, 'year');
  const studentsCount = idParam(params, 'students_count');
  return React.useMemo(() => ({
    ...navigation,
    values: {
      q: stringParam(params, 'q', SEARCH_MAX),
      status: normalizeStatus(params.get('status') ?? ''),
      branch: stringParam(params, 'branch', 100),
      delivery_mode: stringParam(params, 'delivery_mode', 20),
      course: stringParam(params, 'course', 150),
      instructor: idParam(params, 'instructor'),
      dateFrom: dateParam(params, 'dateFrom'),
      dateTo: dateParam(params, 'dateTo'),
      month: validMonth(params.get('month') ?? '') ? params.get('month')! : '',
      year: year && year >= YEAR_MIN && year <= YEAR_MAX ? year : null,
      studentsCount: studentsCount && studentsCount <= COUNT_MAX ? studentsCount : null,
      studentsCountCmp: studentsCount
        ? normalizeComparison(params.get('students_count_cmp') ?? '') || 'more'
        : '',
      page: pageParam(params),
      pageSize: pageSizeParam(params),
      group,
      groupPanel: groupPanelParam(params),
      panel: !group && (panelValue === 'filters' || panelValue === 'create' || panelValue === 'settings')
        ? panelValue
        : '',
    },
  }), [group, navigation, panelValue, params, studentsCount, year]);
}

export function useCalendarNavigation() {
  const canonicalSearch = useCanonicalSearch('calendar');
  const navigation = useNavigationQuery(calendarNavigationSchema);
  const params = React.useMemo(() => new URLSearchParams(canonicalSearch), [canonicalSearch]);
  return React.useMemo(() => ({
    ...navigation,
    values: {
      month: validMonth(params.get('month') ?? '') ? params.get('month')! : '',
      branch: stringParam(params, 'branch', 100),
      delivery_mode: stringParam(params, 'delivery_mode', 20),
      day: dateParam(params, 'day'),
    },
  }), [navigation, params]);
}

export const receptionGroupNavigationInternals = {
  normalizeComparison,
  normalizeStatus,
  normalizedSearch,
  validMonth,
};

export const boundedStudentIdArrayAdapter: SessionStateAdapter<number[]> = Object.freeze({
  initial: () => [],
  encode(value: number[]): SafeSessionValue {
    const encoded = [...new Set(value)]
      .filter((id) => Number.isSafeInteger(id) && id > 0)
      .slice(0, 500)
      .sort((left, right) => left - right);
    assertSafeSessionValue(encoded);
    return encoded;
  },
  decode(value: unknown): number[] | null {
    if (!Array.isArray(value) || value.length > 500) return null;
    if (!value.every((id) => typeof id === 'number' && Number.isSafeInteger(id) && id > 0)) return null;
    return [...new Set(value)].sort((left, right) => left - right);
  },
  isDirty: (value: number[]) => value.length > 0,
});
